import { apiClient } from '@/api/apiClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CreateInstallationPayload, Installation, queryKeyInstallations } from './installations'

const queryKeyPartnerships = 'partnerships'

enum PartnershipStatus {
  CREATED = 'CREATED',
  INVITATION_SENT = 'INVITATION_SENT',
  INVITATION_ACCEPTED = 'INVITATION_ACCEPTED',
  INVITATION_REJECTED = 'INVITATION_REJECTED'
}

enum PartnershipInviterOrganizationType {
  OPERATOR = 'OPERATOR',
  DECLARANT = 'DECLARANT'
}

type PartnershipOperatorDetails = {
  operatorStreet?: string
  operatorStreet2?: string
  operatorStreetNumber?: string
  operatorSubDivision?: string
  operatorPostCode?: string
  operatorPoBox?: string
  operatorCountry?: string
  operatorCity?: string
  operatorContactName?: string
  operatorContactPhoneNumber?: string
  operatorContactEmail?: string
  operatorBusinessRegistrationNumber?: string
}

export type Partnership = {
  id: string
  name: string
  customId?: string
  invitationEmail: string
  invitationSentAt: string
  country: string
  operatorOrganizationId: string
  declarantOrganizationId: string
  status: PartnershipStatus
  invitationOrganizationType: PartnershipInviterOrganizationType
  invitationSenderName: string
  invitationSenderEmail: string
  isOffPlatform: boolean
  createdAt: string
  updatedAt: string | null
  invitationRejectedReason: string | null
  invitationRejectedAt: string | null
} & PartnershipOperatorDetails

type CreatePartnershipPayload = {
  name: string
  customId?: string
  country: string
  isOffPlatform: boolean
} & PartnershipOperatorDetails

type UpdatePartnershipOperatorDetailsPayload = {
  name?: string
  country?: string
} & PartnershipOperatorDetails

export const useGetAllPartnerships = (organizationId: string) => {
  return useQuery({
    queryKey: [queryKeyPartnerships, organizationId],
    queryFn: async () => {
      const response = await apiClient.get<Partnership[]>(
        `/v2/organizations/${organizationId}/partnerships`
      )
      return response.data
    }
  })
}

export const useUpdatePartnershipOperatorDetails = (
  organizationId: string,
  partnershipId: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdatePartnershipOperatorDetailsPayload) => {
      const response = await apiClient.patch<Partnership>(
        `/v2/organizations/${organizationId}/partnerships/${partnershipId}/operator-details`,
        payload
      )

      queryClient.setQueryData<Partnership[]>(
        [queryKeyPartnerships, organizationId],
        oldData =>
          oldData?.map(partnership =>
            partnership.id === partnershipId ? response.data : partnership
          ) || []
      )
      return response.data
    }
  })
}

type CreatePartnershipWithInstallationPayload = {
  partnership: CreatePartnershipPayload
  installation: CreateInstallationPayload
}

type CreatePartnershipWithInstallationResult = {
  partnership: Partnership
  installation: Installation
}

export const useCreatePartnershipWithInstallation = (organizationId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      payload: CreatePartnershipWithInstallationPayload
    ): Promise<CreatePartnershipWithInstallationResult> => {
      const response = await apiClient.post<CreatePartnershipWithInstallationResult>(
        `/v2/organizations/${organizationId}/partnerships-with-installation`,
        payload
      )

      queryClient.setQueryData<Partnership[]>([queryKeyPartnerships, organizationId], oldData => [
        ...(oldData || []),
        response.data.partnership
      ])

      queryClient.setQueryData<Installation[]>(
        [queryKeyInstallations, response.data.partnership.id],
        oldData => [...(oldData || []), response.data.installation]
      )

      return response.data
    }
  })
}

export const useDeletePartnership = (organizationId: string, partnershipId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/v2/organizations/${organizationId}/partnerships/${partnershipId}`)

      queryClient.setQueryData<Partnership[]>(
        [queryKeyPartnerships, organizationId],
        oldData => oldData?.filter(partnership => partnership.id !== partnershipId) || []
      )
    }
  })
}
